import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  analyzeChat,
  generateGraphs,
  generateCodeForAnalysis,
  generateCodeForAnalysisV2,
  generateCodeForSingleBlockScaleOut,
  generateMapOutput,
  generateTextResponse,
  generateLatexResponse,
  generateGraphsV2,
  generateMapOutputV2,
  generateCodeForAnalysisShow,
  getDetailedInstructions,
  analyzeCodeError,
  getScaledOutQuestions,
  getScaledOutQuestionConcise,
} from '../../helpers/AI_functions';
import { makeGraphCodeId, normalizeGraphsToArray, attachGraphCodeId, buildPrevGraphCodeContext } from '../../helpers/graphHelpers';
import CodeDisplay from './CodeDisplay';
import GraphDisplay from "./GraphDisplay";
import LatexDisplay from './LatexDisplay';
import AnalysisBlock from './AnalysisBlock';
import PlanBlock from './PlanBlock';
import TextareaAutosize from 'react-textarea-autosize';
import { getStorage, ref as storageRef, getDownloadURL, uploadBytes } from 'firebase/storage';

import { getFirestore, doc, setDoc, deleteDoc, collection, getDocs, query, orderBy, onSnapshot, writeBatch } from 'firebase/firestore';
import { getDatabase, ref as realtimeDbRef, push, set as setRealtimeData, onChildAdded } from 'firebase/database';
import { normalizeInstructionText } from '../../helpers/generalHelpers';
import {
  allocateTopBlockRanks as allocateTopBlockRanksPure,
  hasBlockRank,
  nextRankAfterBlocks,
  prepareRankedAnalysisBlocks,
  sortAnalysisBlocks,
} from '../../helpers/analysisBlockRanking';
import "./ChatComponent.css"

const makeClientId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `client_${Date.now()}_${Math.floor(Math.random() * 1e9)}`;
};

function ChatComponent({ state, updateState, chatId, chatPathIds = []  }) {
  const [messages, setMessages] = useState([
    { sender: 'WUIGo-Bot', text: 'I am an AI agent to help you with planning for mass evacuation scenarios. We are using a digital twin' },
    { sender: 'WUIGo-Bot', text: 'How can I help you today?' },
  ]);
  const [input, setInput] = useState('');
  const [useAnalysisCodeV2, setUseAnalysisCodeV2] = useState(false);
  const [analysisBlocks, setAnalysisBlocks] = useState([]);
  const [variablesText, setVariablesText] = useState('');
  const [latexContent, setLatexContent] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [, setIsAccessingData] = useState(false);
  const [, setIsGeneratingGraphs] = useState(false);
  const [isRealtimeEnabled] = useState(false);
  const db = getFirestore();
  const storage = getStorage();
  const realtimeDb = useMemo(() => getDatabase(), []);

  // State for the analysis script dialog
  const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
  const [analysisScriptText, setAnalysisScriptText] = useState('');
  const [showVariablesDialog, setShowVariablesDialog] = useState(false);

  // State for "Scale out" dialog
  const [showScaleDialog, setShowScaleDialog] = useState(false);
  const [scaleDialogText, setScaleDialogText] = useState('');
  const [scaleSourceBlock, setScaleSourceBlock] = useState(null);
  const [scaleMode, setScaleMode] = useState('multi');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [analysisFilterTerm, setAnalysisFilterTerm] = useState('');

  // Lazy render the analysis block list so we don't mount dozens of heavy blocks at once.
  const INITIAL_BLOCK_BATCH = 5;   // number of blocks to show when data arrives
  const BATCH_SIZE = 5;            // how many extra blocks to reveal per scroll chunk
  const [visibleBlockCount, setVisibleBlockCount] = useState(INITIAL_BLOCK_BATCH);
  const sentinelRef = useRef(null); // observed element that triggers more renders
  const realtimeUnsubscribeRef = useRef(null);
  const realtimeChatListenerRef = useRef(null);
  const lastSentRealtimeKeysRef = useRef(new Set());
  const realtimeClientIdRef = useRef(makeClientId());
  const nextBlockRankRef = useRef(1);
  const normalizedFilterTerm = analysisFilterTerm.trim().toLowerCase();
  const hasActiveFilter = normalizedFilterTerm.length > 0;
  const generateAnalysisCode = useAnalysisCodeV2
    ? generateCodeForAnalysisV2
    : generateCodeForAnalysis;

  const registerBlockRanks = (blocks) => {
    nextBlockRankRef.current = nextRankAfterBlocks(
      nextBlockRankRef.current,
      blocks
    );
  };

  const allocateTopBlockRanks = (count = 1) => {
    const allocation = allocateTopBlockRanksPure(nextBlockRankRef.current, count);
    nextBlockRankRef.current = allocation.nextRank;
    return allocation.ranks;
  };

  const filteredAnalysisBlocks = useMemo(() => {
    if (!hasActiveFilter) return analysisBlocks;

    return analysisBlocks.filter((block) => {
      const searchableText = [
        block.userQuery,
        block.instructions,
        block.codeOutput,
        block.code,
        block.simulationId,
        Array.isArray(block.selectedLinkIds) ? block.selectedLinkIds.join(' ') : '',
        Array.isArray(block.selectedNodeIds) ? block.selectedNodeIds.join(' ') : '',
        block.contentType,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(normalizedFilterTerm);
    });
  }, [analysisBlocks, hasActiveFilter, normalizedFilterTerm]);

  const setBlockRunStatus = (blockId, runStatus, runStatusText = '') => {
    setAnalysisBlocks((prevBlocks) =>
      prevBlocks.map((block) =>
        String(block.id) === String(blockId)
          ? { ...block, runStatus, runStatusText }
          : block
      )
    );
  };

  const setBlockErrorStatus = (
    blockId,
    runStatusText = 'Error while running this analysis.'
  ) => {
    setBlockRunStatus(blockId, 'error', runStatusText);
  };

  const clearBlockRunStatusIfNotError = (blockId) => {
    setAnalysisBlocks((prevBlocks) =>
      prevBlocks.map((block) => {
        if (String(block.id) !== String(blockId)) return block;
        if (block.runStatus === 'error') return block;
        return { ...block, runStatus: null, runStatusText: '' };
      })
    );
  };

  const makeCodeApiRetryStatusUpdater = (blockId, baseText) => ({
    status,
    errorType,
    nextAttemptNumber,
    totalAttempts,
    delayMs,
  }) => {
    const waitSeconds = Math.max(1, Math.round(delayMs / 1000));
    const reasonText = status
      ? `HTTP ${status}`
      : errorType === 'network_error'
        ? 'network issue'
        : 'temporary error';
    setBlockRunStatus(
      blockId,
      'accessing_data',
      `${baseText} ${reasonText}. Retrying (${nextAttemptNumber}/${totalAttempts}) in ${waitSeconds}s...`
    );
  };

  const activeChatId = chatId || 'a_0000';
  const chatPathKey = chatPathIds.join('>');

  const resolveChatRefs = () => {
    // Build: Analyses/(folder1)/Analyses/(folder2)/Analyses/.../(chatId)
    let col = collection(db, 'Analyses');
    for (const folderId of chatPathIds) {
      const parentDoc = doc(col, folderId);
      col = collection(parentDoc, 'Analyses');
    }
    const chatDoc = doc(col, chatId || 'a_0000');
    const blocksCol = collection(chatDoc, 'Analysis blocks');
    // storage prefix: "Analyses/<folder>/Analyses/<folder>/.../<chatId>"
    const storagePrefix = ['Analyses', ...chatPathIds.flatMap(id => [id, 'Analyses']), chatId || 'a_0000'].join('/');
    return { chatDoc, blocksCol, storagePrefix };
  };

  const buildRealtimeChatPath = () => {
    const nested = Array.isArray(chatPathIds) ? chatPathIds.filter(Boolean) : [];
    return ['chats', ...nested, activeChatId].filter(Boolean).join('/');
  };

  const buildAnalysisAddress = () =>
    ['Analyses', ...chatPathIds.flatMap(id => [id, 'Analyses']), activeChatId].join('/');

  const normalizeAnalysisBlockDoc = (data, docId) => {
    const normalized = { ...(data || {}) };
    const id = normalized.id ?? docId;
    const codeOutput = normalized.codeOutput ?? normalized.code_output ?? null;
    return {
      ...normalized,
      id,
      codeOutput,
      graphs: normalizeGraphsToArray(normalized.graphs),
      graphCodes: Array.isArray(normalized.graphCodes) ? normalized.graphCodes : [],
      mapOutput: normalized.mapOutput || null,
      collapsed: false,
      runStatus: null,
      runStatusText: '',
    };
  };

  const handleBlockClick = (block) => {
    if (block.mapOutput) {
      updateState({
        mapDataText: block.mapOutput.Details || "No details available",
        contraflow: false,
        blockedRoads: 0,
        time: state.time,
        dynamic_data: block.mapOutput.Data || []
      });
    }
  };

  const handleToggleCollapse = (blockToUpdate, newCollapsed) => {
    setAnalysisBlocks((prevBlocks) =>
      prevBlocks.map((b) =>
        b === blockToUpdate ? { ...b, collapsed: newCollapsed } : b
      )
    );
  };

  const saveAnalysisBlock = async (blockObj) => {
    try {
      const { blocksCol } = resolveChatRefs();
      const {
        runStatus,
        runStatusText,
        ...persistableBlock
      } = blockObj || {};
      await setDoc(doc(blocksCol, String(blockObj.id)), persistableBlock, { merge: true });

    } catch (err) {
      console.error('Firestore save error', err);
    }
  };

  const removeAnalysisBlock = async (blockId) => {
    try {
      const { blocksCol } = resolveChatRefs();
      await deleteDoc(doc(blocksCol, String(blockId)));
    } catch (err) {
      console.error('Firestore delete error', err);
    }
  };

  const loadAnalysisBlocks = async () => {
    try {
      const { blocksCol } = resolveChatRefs();
      const q = query(blocksCol, orderBy('timestamp', 'desc'));
      const snap = await getDocs(q);
      const normalizedBlocks = snap.docs.map(d =>
        normalizeAnalysisBlockDoc(d.data(), d.id)
      );
      const prepared = prepareRankedAnalysisBlocks(
        normalizedBlocks,
        nextBlockRankRef.current
      );
      nextBlockRankRef.current = prepared.nextRank;
      setAnalysisBlocks(prepared.blocks);

      if (prepared.migrations.length > 0) {
        const rankBatch = writeBatch(db);
        prepared.migrations.forEach(({ id, rank }) => {
          rankBatch.set(doc(blocksCol, id), { rank }, { merge: true });
        });
        await rankBatch.commit();
      }
    } catch (err) {
      console.error('Firestore load error', err);
    }
  };

  const moveAnalysisBlockToPosition = async (blockToMove, requestedPosition) => {
    if (hasActiveFilter) return;
    const position = Number(requestedPosition);
    if (!Number.isInteger(position) || position < 1 || position > analysisBlocks.length) return;

    const previousBlocks = analysisBlocks;
    const movingId = String(blockToMove.id);
    const movingBlock = analysisBlocks.find((block) => String(block.id) === movingId);
    if (!movingBlock) return;

    const reordered = analysisBlocks.filter((block) => String(block.id) !== movingId);
    reordered.splice(position - 1, 0, movingBlock);
    const rankedBlocks = reordered.map((block, index) => ({
      ...block,
      rank: reordered.length - index,
    }));

    registerBlockRanks(rankedBlocks);
    setAnalysisBlocks(rankedBlocks);
    try {
      const { blocksCol } = resolveChatRefs();
      const batch = writeBatch(db);
      rankedBlocks.forEach((block) => {
        batch.set(
          doc(blocksCol, String(block.id)),
          { rank: block.rank },
          { merge: true }
        );
      });
      await batch.commit();
    } catch (error) {
      console.error('Firestore block position update error', error);
      setAnalysisBlocks(previousBlocks);
    }
  };

  useEffect(() => {
    // Whenever a new chat loads, restart the window so only a few blocks render.
    const nextCount = Math.min(INITIAL_BLOCK_BATCH, filteredAnalysisBlocks.length);
    setVisibleBlockCount(nextCount);
  }, [filteredAnalysisBlocks.length]);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    // As the sentinel comes into view, reveal the next batch of analysis blocks.
    const observer = new IntersectionObserver((entries) => {
      const isVisible = entries.some(entry => entry.isIntersecting);
      if (!isVisible) return;
      setVisibleBlockCount(prev => {
        if (prev >= filteredAnalysisBlocks.length) return prev;
        return Math.min(prev + BATCH_SIZE, filteredAnalysisBlocks.length);
      });
    }, { root: null, threshold: 0.1 });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [filteredAnalysisBlocks.length]);

  const conductAnalysis = async(userQuery, blockId, selectedLinkIds, selectedNodeIds, time, maxTime, selectedSimulationId, rank) => {
    const codeAgentVersion = useAnalysisCodeV2 ? 'v2' : 'v1';
    const assignedRank = rank != null && Number.isFinite(Number(rank))
      ? Number(rank)
      : allocateTopBlockRanks(1)[0];
    setAnalysisBlocks((prevBlocks) => {
      const exists = prevBlocks.some((b) => b.id === blockId);
      if (exists) {
        // Update the existing block: reset its content so new analysis can be loaded.
        return prevBlocks.map((b) =>
          b.id === blockId
            ? {
                ...b,
                userQuery: userQuery,
                instructions: null,
                codeOutput: null,
                code: null,
                graphs: null,
                mapOutput: null,
                rank: assignedRank,
                codeAgentVersion,
                collapsed: false,
                runStatus: 'thinking',
                runStatusText: 'Thinking...',
              }
            : b
        );
      } else {
        // Create a new block if one doesn't already exist.
        const newBlock = {
          id: blockId,
          userQuery: userQuery, // Original query as heading
          instructions: null,
          simulationId: selectedSimulationId,
          selectedLinkIds: Array.from(selectedLinkIds),
          selectedNodeIds: Array.from(selectedNodeIds),
          codeOutput: null,
          code: null,
          graphs: null,
          graphCodes: [],
          mapOutput: null,
          rank: assignedRank,
          codeAgentVersion,
          collapsed: false,
          runStatus: 'thinking',
          runStatusText: 'Thinking...',
        };
        return [newBlock, ...prevBlocks];
      }
    });

    try {
      let model_context =
        "{current_time: " +
        String(time) +
        // ", max_simulation_time: " +
        // String(maxTime) +
        ",\n" +
        "Selected linkIds: [" +
        Array.from(selectedLinkIds).join(", ") +
        "]\n" +
        "Selected nodeIds: [" +
        Array.from(selectedNodeIds).join(", ") +
        "]\n" +
        "}";

      setBlockRunStatus(blockId, 'thinking', 'Thinking...');
      let analysisInstructions = await getDetailedInstructions(userQuery + "\n model_context: "+ model_context);
      console.log("step 2: ", analysisInstructions)

      // Clean up the detailed instructions.
      let detailedInstructions = analysisInstructions;
      let detailedInstructions_show = normalizeInstructionText(detailedInstructions, {
        stripStepLabels: true,
      });

      setAnalysisBlocks((prevBlocks) =>
        prevBlocks.map((b) =>
          b.id === blockId
            ? { ...b, instructions: detailedInstructions_show }
            : b
        ))

      // Get code output and generated code.
      setIsAccessingData(true);
      setBlockRunStatus(blockId, 'accessing_data', 'Writing code to pull relevant data...');
      let responseData = await generateAnalysisCode(
        detailedInstructions,
        selectedSimulationId,
        blockId,
        variablesText,
        selectedLinkIds,
        selectedNodeIds,
        makeCodeApiRetryStatusUpdater(blockId, 'Writing code to pull relevant data...'),
        buildAnalysisAddress(),
        {
          userQuery,
          instructions: detailedInstructions_show,
          timestamp: Date.now()
        }
      );
      setIsAccessingData(false);

      let extractedCode = responseData.code;
      let extractedOutput = responseData.code_output || '';

      if (extractedOutput) {
        setAnalysisBlocks((prevBlocks) =>
          prevBlocks.map((b) =>
            b.id === blockId
              ? { ...b, codeOutput: extractedOutput, code: extractedCode }
              : b
          )
        );
      }

      console.log("First point", extractedOutput)

      if (codeAgentVersion === 'v1' && extractedOutput.includes("Error")) {
        // Run it through error agent to correct it
        setAnalysisBlocks(prev =>
          prev.map(b => b.id === blockId ? { ...b, codeOutput: null } : b)
        );

        setIsAccessingData(true);
        setBlockRunStatus(blockId, 'accessing_data', 'Fixing code errors...');
        responseData = await analyzeCodeError(
          detailedInstructions,
          selectedSimulationId,
          blockId,
          extractedCode,
          extractedOutput,
          variablesText,
          selectedLinkIds,
          selectedNodeIds,
          makeCodeApiRetryStatusUpdater(blockId, 'Fixing code errors...')
        );
        setIsAccessingData(false);

        extractedCode = responseData.code;
        extractedOutput = responseData.code_output || '';

        if (extractedOutput) {
          setAnalysisBlocks((prevBlocks) =>
            prevBlocks.map((b) =>
              b.id === blockId
                ? { ...b, codeOutput: extractedOutput, code: extractedCode }
                : b
            )
          );
        }
      }

      console.log("Second point", extractedOutput)

      if (extractedOutput) {
        setAnalysisBlocks((prevBlocks) =>
          prevBlocks.map((b) =>
            b.id === blockId
              ? { ...b, codeOutput: extractedOutput, code: extractedCode }
              : b
          )
        );

        // In parallel, fetch graphs, map output, and text response.
        if (!extractedOutput.includes("Error")) {
          setIsGeneratingGraphs(true);
          setBlockRunStatus(blockId, 'generating_outputs', 'Generating graphs and map output...');
          const [graphsRes, mapResult, textResult] = await Promise.all([
            generateGraphsV2(userQuery, extractedOutput, extractedCode),
            generateMapOutputV2(detailedInstructions, extractedOutput, extractedCode),
            generateTextResponse(detailedInstructions, extractedOutput),
          ]);

          const codeId = makeGraphCodeId();
          const graphsArr = attachGraphCodeId(
            normalizeGraphsToArray(graphsRes?.graphData),
            codeId
          );

          const graphCodesArr = graphsRes?.graphCode
            ? [{ id: codeId, code: graphsRes.graphCode }]
            : [];
          setIsGeneratingGraphs(false);

          if (mapResult?.graphData) {
            updateState({
              mapDataText: mapResult.graphData.Details || "No details available",
              dynamic_data: mapResult.graphData.Data || []
            });
          }

          const completedBlock = {
            id: blockId,
            userQuery: userQuery,
            instructions: detailedInstructions_show,
            simulationId: selectedSimulationId,
            selectedLinkIds: Array.from(selectedLinkIds),
            selectedNodeIds: Array.from(selectedNodeIds),
            code: extractedCode,
            codeOutput: extractedOutput,
            rank: assignedRank,
            codeAgentVersion,
            graphs: graphsArr,
            graphCodes: graphCodesArr,
            mapOutput: mapResult?.graphData || null,
            timestamp: Date.now(),
          };
          saveAnalysisBlock(completedBlock);

          setAnalysisBlocks((prevBlocks) =>
            prevBlocks.map((b) =>
              b.id === blockId
                ? {
                    ...b,
                    graphs: graphsArr,
                    graphCodes: graphCodesArr,
                    mapOutput: mapResult?.graphData || null,
                  }
                : b
            )
          );

          if (textResult?.textResponse) {
            setMessages((prevMessages) => [
              ...prevMessages,
              { sender: 'WUIGo-Bot', text: String(textResult.textResponse) },
            ]);
          }
        } else {
          setBlockErrorStatus(blockId, 'Code execution finished with errors.');
        }
      } else {
        setBlockErrorStatus(blockId, 'No code output was generated.');
      }
    } catch (err) {
      console.error('Error running analysis:', err);
      setBlockErrorStatus(blockId, 'Error while running analysis.');
    } finally {
      setIsAccessingData(false);
      setIsGeneratingGraphs(false);
      clearBlockRunStatusIfNotError(blockId);
    }
  }

  const handleClearFilter = () => {
    setAnalysisFilterTerm('');
    setShowFilterPanel(false);
  };

  const handleCancelFilter = () => {
    setAnalysisFilterTerm('');
    setShowFilterPanel(false);
  };

  // SCALE-OUT PIPELINE
  const runScaleOutAnalysis = async (sourceBlock, newBlockId, newMessage, options = {}) => {
    const {
      conciseVariables = null,
      includeOldCodeInInstructions = false,
      singleBlock = false,
      rank = null,
    } = options || {};
    const codeAgentVersion = useAnalysisCodeV2 ? 'v2' : 'v1';
    const assignedRank = rank != null && Number.isFinite(Number(rank))
      ? Number(rank)
      : null;
    let preserveRunStatus = false;
    setAnalysisBlocks((prevBlocks) =>
      prevBlocks.map((block) =>
        block.id === newBlockId
          ? {
              ...block,
              ...(assignedRank == null ? {} : { rank: assignedRank }),
              codeAgentVersion,
            }
          : block
      )
    );
    // Use either the original block selection, or current UI selection as fallback
    const selectedLinkIds = new Set(
      sourceBlock.selectedLinkIds || state.selectedLinkIds || []
    );
    const selectedNodeIds = new Set(
      sourceBlock.selectedNodeIds || state.selectedNodeIds || []
    );
    const time = state.time;
    const maxTime = state.maxTime;
    const selectedSimulationId =
      sourceBlock.simulationId || state.selectedSimulationId;

    try {
      let model_context =
        "{current_time: " +
        String(time) +
        // ", max_simulation_time: " +
        // String(maxTime) +
        ",\n" +
        "Selected linkIds: [" +
        Array.from(selectedLinkIds).join(", ") +
        "]\n" +
        "Selected nodeIds: [" +
        Array.from(selectedNodeIds).join(", ") +
        "]\n" +
        "}";

      // 1) Build instructions prompt using old instructions + new message
      const instructionsPrompt =
        `<Old instructions>${sourceBlock.instructions || ''}</Old instructions>\n` +
        (includeOldCodeInInstructions
          ? `<Old code>${sourceBlock.code || ''}</Old code>\n`
          : '') +
        `New message: ${newMessage}` +
        (conciseVariables
          ? `\nVariables: ${JSON.stringify(conciseVariables)}`
          : '');

      setBlockRunStatus(newBlockId, 'thinking', 'Thinking...');
      let analysisInstructions = await getDetailedInstructions(
        instructionsPrompt + "\n model_context: " + model_context
      );

      // Clean up the detailed instructions (same as conductAnalysis)
      let detailedInstructions = analysisInstructions;
      let detailedInstructions_show = normalizeInstructionText(detailedInstructions, {
        stripStepLabels: true,
      });

      setAnalysisBlocks((prevBlocks) =>
        prevBlocks.map((b) =>
          b.id === newBlockId ? { ...b, instructions: detailedInstructions_show } : b
        )
      );

      // 2) Build coding prompt using old instructions, old code and the new instructions
      const codingPrompt =
        "Additional context:\n" +
        `<Old instructions>${sourceBlock.instructions || ""}</Old instructions>\n` +
        `<Old code>${sourceBlock.code || ""}</Old code>\n` +
        `<New instructions>${detailedInstructions}</New instructions>\n` +
        (conciseVariables
          ? `<Variables>${JSON.stringify(conciseVariables)}</Variables>\n`
          : '') +
        "Please provide the latest code";

      // 3) Generate code and output
      setIsAccessingData(true);
      setBlockRunStatus(newBlockId, 'accessing_data', 'Writing code to pull relevant data...');
      const codeRequestTimestamp = Date.now();
      const retryStatusUpdater = makeCodeApiRetryStatusUpdater(
        newBlockId,
        'Writing code to pull relevant data...'
      );
      let responseData = singleBlock
        ? await generateCodeForSingleBlockScaleOut({
            instruction: codingPrompt,
            selectedSimulationId,
            blockId: newBlockId,
            analysisAddress: buildAnalysisAddress(),
            userQuery: newMessage,
            instructions: detailedInstructions_show,
            selectedLinkIds: Array.from(selectedLinkIds),
            selectedNodeIds: Array.from(selectedNodeIds),
            timestamp: codeRequestTimestamp,
            useV2: useAnalysisCodeV2,
          }, retryStatusUpdater)
        : await generateAnalysisCode(
            codingPrompt,
            selectedSimulationId,
            newBlockId,
            variablesText,
            selectedLinkIds,
            selectedNodeIds,
            retryStatusUpdater,
            buildAnalysisAddress(),
            {
              userQuery: newMessage,
              instructions: detailedInstructions_show,
              timestamp: codeRequestTimestamp
            }
          );
      setIsAccessingData(false);

      if (
        singleBlock &&
        responseData?.ok === true &&
        responseData?.status === 'submitted'
      ) {
        preserveRunStatus = true;
        setBlockRunStatus(
          newBlockId,
          'accessing_data',
          'Code generation job started'
        );
        return;
      }

      let extractedCode = responseData.code;
      let extractedOutput = responseData.code_output || '';

      if (extractedOutput) {
        setAnalysisBlocks((prevBlocks) =>
          prevBlocks.map((b) =>
            b.id === newBlockId
              ? { ...b, codeOutput: extractedOutput, code: extractedCode }
              : b
          )
        );
      }

      console.log("Scale-out first point", extractedOutput);

      // 4) If error in the first run, send through error-correction agent
      if (
        codeAgentVersion === 'v1' &&
        extractedOutput &&
        extractedOutput.includes("Error")
      ) {
        setAnalysisBlocks((prev) =>
          prev.map((b) =>
            b.id === newBlockId ? { ...b, codeOutput: null } : b
          )
        );

        setIsAccessingData(true);
        setBlockRunStatus(newBlockId, 'accessing_data', 'Fixing code errors...');
        responseData = await analyzeCodeError(
          detailedInstructions,
          selectedSimulationId,
          newBlockId,
          extractedCode,
          extractedOutput,
          variablesText,
          selectedLinkIds,
          selectedNodeIds,
          makeCodeApiRetryStatusUpdater(newBlockId, 'Fixing code errors...')
        );
        setIsAccessingData(false);

        extractedCode = responseData.code;
        extractedOutput = responseData.code_output || '';

        if (extractedOutput) {
          setAnalysisBlocks((prevBlocks) =>
            prevBlocks.map((b) =>
              b.id === newBlockId
                ? { ...b, codeOutput: extractedOutput, code: extractedCode }
                : b
            )
          );
        }
      }

      console.log("Scale-out second point", extractedOutput);

      // 5) If we now have valid output, regenerate graphs, map and text
      if (extractedOutput) {
        setAnalysisBlocks((prevBlocks) =>
          prevBlocks.map((b) =>
            b.id === newBlockId
              ? { ...b, codeOutput: extractedOutput, code: extractedCode }
              : b
          )
        );

        if (!extractedOutput.includes("Error")) {
          setIsGeneratingGraphs(true);
          setBlockRunStatus(newBlockId, 'generating_outputs', 'Generating graphs and map output...');
          const graphInstr =
            newMessage +
            buildPrevGraphCodeContext(sourceBlock);

          const [graphsRes, mapResult, textResult] = await Promise.all([
            generateGraphsV2(graphInstr, extractedOutput, extractedCode),
            generateMapOutputV2(detailedInstructions, extractedOutput, extractedCode),
            generateTextResponse(detailedInstructions, extractedOutput),
          ]);

          const codeId = makeGraphCodeId();
          const graphsArr = attachGraphCodeId(
            normalizeGraphsToArray(graphsRes?.graphData),
            codeId
          );

          const graphCodesArr = graphsRes?.graphCode
            ? [{ id: codeId, code: graphsRes.graphCode }]
            : [];
          setIsGeneratingGraphs(false);

          if (mapResult?.graphData) {
            updateState({
              mapDataText: mapResult.graphData.Details || "No details available",
              dynamic_data: mapResult.graphData.Data || []
            });
          }

          const completedBlock = {
            id: newBlockId,
            userQuery: newMessage,
            variables: conciseVariables || null,
            instructions: detailedInstructions_show,
            simulationId: selectedSimulationId,
            selectedLinkIds: Array.from(selectedLinkIds),
            selectedNodeIds: Array.from(selectedNodeIds),
            code: extractedCode,
            codeOutput: extractedOutput,
            ...(assignedRank == null ? {} : { rank: assignedRank }),
            codeAgentVersion,
            graphs: graphsArr,
            graphCodes: graphCodesArr,
            mapOutput: mapResult?.graphData || null,
            timestamp: Date.now(),
          };
          saveAnalysisBlock(completedBlock);

          setAnalysisBlocks((prevBlocks) =>
            prevBlocks.map((b) =>
              b.id === newBlockId
                ? {
                    ...b,
                    graphs: graphsArr,
                    graphCodes: graphCodesArr,
                    mapOutput: mapResult?.graphData || null,
                    variables: conciseVariables || b.variables || null,
                  }
                : b
            )
          );

          if (textResult?.textResponse) {
            setMessages((prevMessages) => [
              ...prevMessages,
              { sender: "WUIGo-Bot", text: String(textResult.textResponse) }
            ]);
          }
        } else {
          setBlockErrorStatus(newBlockId, 'Code execution finished with errors.');
        }
      } else {
        setBlockErrorStatus(newBlockId, 'No code output was generated.');
      }
    } catch (err) {
      console.error('Error during scale-out analysis:', err);
      setBlockErrorStatus(newBlockId, 'Error while running analysis.');
      throw err;
    } finally {
      setIsAccessingData(false);
      setIsGeneratingGraphs(false);
      if (!preserveRunStatus) {
        clearBlockRunStatusIfNotError(newBlockId);
      }
    }
  };

  // Common function to process a user query (used by both chat input and analysis script)
  const processUserQuery = async (userQuery) => {
    let prev_messages = JSON.stringify(messages);

    // Append the user's query as a chat message
    setMessages((prevMessages) => [
      ...prevMessages,
      { sender: 'User', text: userQuery }
    ]);

    setInput('')

    //Get state
    let selectedNodeIds = state.selectedNodeIds
    let selectedLinkIds = state.selectedLinkIds
    let time = state.time
    let maxTime = state.maxTime
    let selectedSimulationId = state.selectedSimulationId

    try {
      setIsThinking(true);
      let current_message = userQuery.trim();
      
      let analyzedChatResult = await analyzeChat(current_message, "", prev_messages);
      let chatResponse = analyzedChatResult.final_result;
      setIsThinking(false);
      console.log("step 1: ", chatResponse)

      if (chatResponse.isAnalysis) {

        const blockId = Date.now();
        const rank = allocateTopBlockRanks(1)[0];
        conductAnalysis(current_message, blockId, selectedLinkIds, selectedNodeIds, time, maxTime, selectedSimulationId, rank)

      } else {
        setMessages((prevMessages) => [
          ...prevMessages,
          { sender: 'WUIGo-Bot', text: `${chatResponse.response}` },
        ]);
      }
    } catch (error) {
      console.error('Error processing query:', error);
      setIsThinking(false);
      setIsGeneratingGraphs(false);
      setIsAccessingData(false);
      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: 'WUIGo-Bot', text: 'Sorry, there was an error processing your request.' },
      ]);
    }
  };

  const sendRealtimeMessage = async (text) => {
    try {
      const realtimePath = buildRealtimeChatPath();
      if (!realtimePath) return;
      const chatRef = realtimeDbRef(realtimeDb, realtimePath);
      const messageRef = push(chatRef);
      const payload = {
        sender: 'User',
        text,
        timestamp: Date.now(),
        clientId: realtimeClientIdRef.current,
      };
      if (messageRef?.key) {
        lastSentRealtimeKeysRef.current.add(messageRef.key);
      }
      setMessages((prev) => [...prev, payload]);
      await setRealtimeData(messageRef, payload);
    } catch (err) {
      console.error('Realtime chat send error:', err);
    }
  };

  // Handler for the chat send button
  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    if (isRealtimeEnabled) {
      await sendRealtimeMessage(trimmed);
    } else {
      await processUserQuery(trimmed);
    }
    setInput('');
  };

  // Handler for saving the analysis script from the dialog
  const handleSaveAnalysisScript = async () => {

    console.log(analysisScriptText)

    if (analysisScriptText.trim()) {
      // Split instructions by double new line
      const blocks = analysisScriptText.split("\n\n").map(block => block.trim()).filter(block => block);
      console.log(blocks);
      for (const block of blocks) {
        console.log(block)
        processUserQuery(block);
      }
      setAnalysisScriptText('');
      setShowAnalysisDialog(false);
    }
  };

  const loadGlobalVariables = async () => {
    try {
      const { storagePrefix } = resolveChatRefs();
      const fileRef = storageRef(storage, `${storagePrefix}/variables/global_var.txt`);
      const url  = await getDownloadURL(fileRef);
      const text = await (await fetch(url)).text();
      setVariablesText(text);                 // show existing content
    } catch (err) {
      setVariablesText('');                   // no file → blank textarea
    }
  };
  
  const saveGlobalVariables = async () => {
    const fileRef = storageRef(
      storage,
      `Analyses/${activeChatId}/variables/global_var.txt`
    );
    const blob = new Blob([variablesText], { type: 'text/plain' });
    await uploadBytes(fileRef, blob);         // overwrite or create
    setShowVariablesDialog(false);
  };

  const handleLatexConversion = async () => {
    let latextInput = {
      textInput: messages,
      graphs: analysisBlocks.flatMap((block) => block.graphs ? [block.graphs] : [])
    };

    try {
      let latexResponse = await generateLatexResponse(latextInput);
      setLatexContent(latexResponse.latexResponse);
    } catch (error) {
      console.error('Error calling cloud function:', error);
    }
  };

  const handleRefresh = (block) => {
    conductAnalysis(
      block.userQuery,
      block.id,
      block.selectedLinkIds || [],
      block.selectedNodeIds || [],
      state.time,
      state.maxTime,
      block.simulationId || state.selectedSimulationId,
      block.rank
    );
  };

  const addGraphOnly = async (blk, extra = '') => {
    if (!blk.codeOutput || !blk.code) return;

    try {
      setIsGeneratingGraphs(true);
      setBlockRunStatus(blk.id, 'generating_outputs', 'Generating additional graph...');

      const prevGraphCtx = buildPrevGraphCodeContext(blk);
      const augmentedQuery = extra
        ? `${blk.userQuery}\nAdditional context: ${extra}${prevGraphCtx}`
        : `${blk.userQuery}${prevGraphCtx}`;

      const result = await generateGraphsV2(augmentedQuery, blk.codeOutput, blk.code);

      const newGraphsRaw = normalizeGraphsToArray(result.graphData);
      if (newGraphsRaw.length === 0) return;

      const codeId = makeGraphCodeId();
      const newGraphs = attachGraphCodeId(newGraphsRaw, codeId);

      const existingGraphs = Array.isArray(blk.graphs) ? blk.graphs : [];
      const updatedGraphs = [...existingGraphs, ...newGraphs];

      const existingCodes = Array.isArray(blk.graphCodes) ? blk.graphCodes : [];
      const updatedCodes = result.graphCode
        ? [...existingCodes, { id: codeId, code: result.graphCode }]
        : existingCodes;

      setAnalysisBlocks(prev =>
        prev.map(b =>
          b.id === blk.id
            ? { ...b, graphs: updatedGraphs, graphCodes: updatedCodes }
            : b
        )
      );

      saveAnalysisBlock({
        ...blk,
        graphs: updatedGraphs,
        graphCodes: updatedCodes,
        timestamp: Date.now(),
      });
    } catch (err) {
      console.error('Error generating additional graph:', err);
      setBlockErrorStatus(blk.id, 'Error while generating graph.');
    } finally {
      setIsGeneratingGraphs(false);
      clearBlockRunStatusIfNotError(blk.id);
    }
  };
  
  const refreshMapOnly = async (blk, extraNote = '') => {
    if (!blk.codeOutput || !blk.code || !blk.instructions) return;
    try {
      setIsGeneratingGraphs(true);
      setBlockRunStatus(blk.id, 'generating_outputs', 'Regenerating map output...');

      const mapInstr = extraNote.trim()
        ? `${blk.instructions}\n\nAdditional context: ${extraNote.trim()}`
        : blk.instructions;
      const mapRes = await generateMapOutputV2(
        mapInstr,
        blk.codeOutput,
        blk.code
      );

      if (mapRes?.graphData) {
        updateState({
          mapDataText: mapRes.graphData.Details || "No details available",
          dynamic_data: mapRes.graphData.Data || [],
        });
      }

      setAnalysisBlocks((prev) =>
        prev.map((b) => (b.id === blk.id ? { ...b, mapOutput: mapRes?.graphData || null } : b))
      );
      saveAnalysisBlock({ ...blk, mapOutput: mapRes?.graphData || null, timestamp: Date.now() });
    } catch (err) {
      console.error('Error regenerating map output:', err);
      setBlockErrorStatus(blk.id, 'Error while regenerating map output.');
    } finally {
      setIsGeneratingGraphs(false);
      clearBlockRunStatusIfNotError(blk.id);
    }
  };

  const handleDelete = (block) => {
    setAnalysisBlocks((prevBlocks) => prevBlocks.filter(b => b.id !== block.id));
    removeAnalysisBlock(block.id);
  };

  const handleDeleteGraph = (blk, idx) => {
    const existingGraphs = Array.isArray(blk.graphs) ? blk.graphs : [];
    if (idx < 0 || idx >= existingGraphs.length) return;
  
    const graphToRemove = existingGraphs[idx];
    const codeId = graphToRemove?._codeId;
  
    const updatedGraphs = existingGraphs.filter((_, i) => i !== idx);
  
    let updatedCodes = Array.isArray(blk.graphCodes) ? blk.graphCodes : [];
    if (codeId) {
      const stillUsed = updatedGraphs.some(g => g?._codeId === codeId);
      if (!stillUsed) {
        updatedCodes = updatedCodes.filter(c => c.id !== codeId);
      }
    }
  
    setAnalysisBlocks(prev =>
      prev.map(b => b.id === blk.id ? { ...b, graphs: updatedGraphs, graphCodes: updatedCodes } : b)
    );
  
    saveAnalysisBlock({
      ...blk,
      graphs: updatedGraphs,
      graphCodes: updatedCodes,
      timestamp: Date.now(),
    });
  };

  const refreshCodeOnly = async (blk, extra='') => {
    const codeAgentVersion = useAnalysisCodeV2 ? 'v2' : 'v1';
    try {
      setIsAccessingData(true);
      setBlockRunStatus(blk.id, 'accessing_data', 'Refreshing code output...');

      // rebuild the full instruction string
      const augmentedInstr =
        blk.instructions +
        (extra.trim() ? `\n\nAdditional instructions: ${extra.trim()}` : '');


      setAnalysisBlocks(prev =>
        prev.map(b =>
          b.id === blk.id
            ? { ...b, code: null, codeOutput: null, graphs: null, mapOutput: null, codeAgentVersion }
            : b
        )
      );
    
      // 1. get fresh code
      const { code, code_output } =
        await generateAnalysisCode(
          augmentedInstr,
          blk.simulationId,
          blk.id,
          variablesText,
          blk.selectedLinkIds || [],
          blk.selectedNodeIds || [],
          makeCodeApiRetryStatusUpdater(blk.id, 'Refreshing code output...'),
          buildAnalysisAddress(),
          {
            userQuery: blk.userQuery,
            instructions: augmentedInstr,
            timestamp: Date.now()
          }
        );
    
      // 2. update block immediately
      setAnalysisBlocks(prev =>
        prev.map(b =>
          b.id === blk.id
            ? { ...b, code, codeOutput: code_output, graphs: null, mapOutput: null, codeAgentVersion }
            : b
        )
      );
    
      // 3. if no error → regenerate visuals
      if (!code_output.includes('Error')) {
        await regenerateVisuals(blk.id, augmentedInstr, code_output, code);
      } else {
        setBlockErrorStatus(blk.id, 'Code execution finished with errors.');
      }
      // 4. persist
      saveAnalysisBlock({ ...blk, code, codeOutput: code_output, codeAgentVersion, timestamp: Date.now() });
    } catch (err) {
      console.error('Error refreshing code output:', err);
      setBlockErrorStatus(blk.id, 'Error while refreshing code output.');
    } finally {
      setIsAccessingData(false);
      clearBlockRunStatusIfNotError(blk.id);
    }
  };
  
  const fixCodeOnly = async (blk, extra='') => {
    if (blk.codeAgentVersion === 'v2') return;

    try {
      setIsAccessingData(true);
      setBlockRunStatus(blk.id, 'accessing_data', 'Fixing code output...');

      let oldCode = blk.code;
      let oldOutput = blk.codeOutput

      setAnalysisBlocks(prev =>
        prev.map(b =>
          b.id === blk.id
            ? { ...b, code: null, codeOutput: null, graphs: null, mapOutput: null }
            : b
        )
      );
      
      const augmentedInstr =
        blk.instructions +
        (extra.trim() ? `\n\nAdditional instructions: ${extra.trim()}` : '');

      console.log(augmentedInstr)
    
      const { code, code_output } = await analyzeCodeError(
        augmentedInstr,
        blk.simulationId,
        blk.id,
        oldCode,
        oldOutput,
        variablesText,
        blk.selectedLinkIds || [],
        blk.selectedNodeIds || [],
        makeCodeApiRetryStatusUpdater(blk.id, 'Fixing code output...')
      );

      console.log(code, code_output)
    
      setAnalysisBlocks(prev =>
        prev.map(b =>
          b.id === blk.id
            ? { ...b, code, codeOutput: code_output, graphs: null, mapOutput: null }
            : b
        )
      );
    
      if (!code_output.includes('Error')) {
        await regenerateVisuals(blk.id, augmentedInstr, code_output, code);
      } else {
        setBlockErrorStatus(blk.id, 'Code execution finished with errors.');
      }
      saveAnalysisBlock({ ...blk, code, codeOutput: code_output, timestamp: Date.now() });
    } catch (err) {
      console.error('Error fixing code output:', err);
      setBlockErrorStatus(blk.id, 'Error while fixing code output.');
    } finally {
      setIsAccessingData(false);
      clearBlockRunStatusIfNotError(blk.id);
    }
  };

  const regenerateVisuals = async (blockId, instr, codeOut, genCode) => {
    try {
      setIsGeneratingGraphs(true);
      setBlockRunStatus(blockId, 'generating_outputs', 'Generating graphs and map output...');

      const [graphsRes, mapRes, textRes] = await Promise.all([
        generateGraphsV2(instr, codeOut, genCode),
        generateMapOutputV2(instr, codeOut, genCode),
        generateTextResponse(instr, codeOut),
      ]);

      const codeId = makeGraphCodeId();
      const graphsArr = attachGraphCodeId(
        normalizeGraphsToArray(graphsRes?.graphData),
        codeId
      );

      const graphCodesArr = graphsRes?.graphCode
        ? [{ id: codeId, code: graphsRes.graphCode }]
        : [];

      setAnalysisBlocks(prev =>
        prev.map(b =>
          b.id === blockId
            ? {
                ...b,
                graphs: graphsArr,
                graphCodes: graphCodesArr,
                mapOutput: mapRes.graphData || null,
              }
            : b
        )
      );

      if (mapRes?.graphData) {
        updateState({
          mapDataText: mapRes.graphData.Details || 'No details available',
          dynamic_data: mapRes.graphData.Data || []
        });
      }

      if (textRes?.textResponse) {
        setMessages(prev => [...prev, { sender: 'WUIGo-Bot', text: textRes.textResponse }]);
      }

      const blk = analysisBlocks.find(b => b.id === blockId);
      if (blk) {
        saveAnalysisBlock({
          ...blk,
          graphs: graphsArr,
          graphCodes: graphCodesArr,
          mapOutput: mapRes.graphData || null,
          timestamp: Date.now(),
        });
      }
    } finally {
      setIsGeneratingGraphs(false);
    }
  };

  // OPEN SCALE-OUT DIALOG FOR A BLOCK
  const handleScaleOutClick = (block) => {
    setScaleSourceBlock(block);
    setScaleDialogText('');
    setShowScaleDialog(true);
  };

  // SUBMIT SCALE-OUT REQUEST
  const handleSaveScaleOut = async () => {
    if (!scaleDialogText.trim() || !scaleSourceBlock) {
      setShowScaleDialog(false);
      return;
    }
  
    const newMessage = scaleDialogText.trim();
    const sourceBlock = scaleSourceBlock;
    const selectedScaleMode = scaleMode;
  
    // 1. Ask backend to expand the request
    setIsThinking(true);
    setShowScaleDialog(false);
    setScaleDialogText('');

    if (selectedScaleMode === 'single') {
      const { final_result } = await getScaledOutQuestionConcise(
        sourceBlock.userQuery,
        newMessage
      );
      setIsThinking(false);
      setScaleSourceBlock(null);

      setMessages((prevMessages) => [
        ...prevMessages,
        { sender: 'User', text: newMessage }
      ]);

      const conciseQuestion = String(final_result?.question || '').trim();
      const conciseVariables = final_result?.variables || {};

      if (!conciseQuestion) {
        setMessages((prev) => [
          ...prev,
          {
            sender: 'WUIGo-Bot',
            text: 'I could not generate a concise follow-up question for this scale-out request.',
          },
        ]);
        return;
      }

      const selectedLinkIds =
        sourceBlock.selectedLinkIds || state.selectedLinkIds || [];
      const selectedNodeIds =
        sourceBlock.selectedNodeIds || state.selectedNodeIds || [];
      const simulationId =
        sourceBlock.simulationId || state.selectedSimulationId;
      const blockId = Date.now();
      const rank = allocateTopBlockRanks(1)[0];

      setAnalysisBlocks((prevBlocks) => [
        {
          id: blockId,
          userQuery: conciseQuestion,
          variables: conciseVariables,
          instructions: null,
          simulationId,
          selectedLinkIds: Array.from(selectedLinkIds),
          selectedNodeIds: Array.from(selectedNodeIds),
          codeOutput: null,
          code: null,
          graphs: null,
          graphCodes: [],
          mapOutput: null,
          rank,
          collapsed: false,
          runStatus: 'queued',
          runStatusText: 'Queued...',
        },
        ...prevBlocks,
      ]);

      await runScaleOutAnalysis(sourceBlock, blockId, conciseQuestion, {
        singleBlock: true,
        rank,
        conciseVariables,
        includeOldCodeInInstructions: true,
      }).catch((error) => {
        console.error('Error during single-block scale-out analysis:', error);
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            sender: 'WUIGo-Bot',
            text: `Sorry, there was an error processing the scale-out question: "${conciseQuestion}".`,
          },
        ]);
      });

      return;
    }
    
    const { final_result } = await getScaledOutQuestions(
      sourceBlock.userQuery, // originalQuestion
      newMessage             // newRequest from dialog
    );
    setIsThinking(false);
  
    const questions = final_result?.questions || [];
    console.log("Scaled questions", questions);
  
    // 2. Close dialog + clear local scale state
    setScaleSourceBlock(null);
  
    // 3. Log the user's scale-out request once in the chat
    setMessages((prevMessages) => [
      ...prevMessages,
      { sender: 'User', text: newMessage }
    ]);
  
    // If nothing came back, just stop
    if (!questions.length) {
      setMessages((prev) => [
        ...prev,
        {
          sender: 'WUIGo-Bot',
          text: 'I could not generate any follow-up questions for this scale-out request.',
        },
      ]);
      return;
    }
  
    // 4. Common context for all new blocks
    const selectedLinkIds =
      sourceBlock.selectedLinkIds || state.selectedLinkIds || [];
    const selectedNodeIds =
      sourceBlock.selectedNodeIds || state.selectedNodeIds || [];
    const simulationId =
      sourceBlock.simulationId || state.selectedSimulationId;
  
    // Use a base timestamp so IDs stay consistent between setAnalysisBlocks and runScaleOutAnalysis
    const baseId = Date.now();
    const newBlockRanks = allocateTopBlockRanks(questions.length);
  
    // 5. Pre-create one analysis block per question
    setAnalysisBlocks((prevBlocks) => {
      const newBlocks = questions.map((q, idx) => ({
        id: baseId + idx,
        rank: newBlockRanks[idx],
        userQuery: q,                 // each question becomes its own block's query
        instructions: null,
        simulationId,
        selectedLinkIds: Array.from(selectedLinkIds),
        selectedNodeIds: Array.from(selectedNodeIds),
        codeOutput: null,
        code: null,
        graphs: null,
        graphCodes: [],
        mapOutput: null,
        collapsed: false,
        runStatus: 'queued',
        runStatusText: 'Queued...',
      }));
      return [...newBlocks, ...prevBlocks];
    });
  
    const batchSize = 5;
    for (let start = 0; start < questions.length; start += batchSize) {
      const batch = questions.slice(start, start + batchSize);
  
      const batchPromises = batch.map((q, offset) => {
        const idx = start + offset;      // global index within questions[]
        const blockId = baseId + idx;    // consistent with pre-created blocks
  
        return runScaleOutAnalysis(sourceBlock, blockId, q, {
          rank: newBlockRanks[idx],
        }).catch((error) => {
          console.error('Error during scale-out analysis:', error);
          setMessages((prevMessages) => [
            ...prevMessages,
            {
              sender: 'WUIGo-Bot',
              text: `Sorry, there was an error processing one of the scale-out questions: "${q}".`,
            },
          ]);
        });
      });
  
      // Wait until this batch of up to 5 is done, then move on
      await Promise.all(batchPromises);
    }
  
  };

  useEffect(() => {
    nextBlockRankRef.current = 1;
    setAnalysisBlocks([]);
    loadAnalysisBlocks();
    loadGlobalVariables();
    // re-run when chat doc *or* its parent folders change
  }, [chatId, chatPathKey]);

  useEffect(() => {
    if (!isRealtimeEnabled) {
      if (realtimeChatListenerRef.current) {
        realtimeChatListenerRef.current();
        realtimeChatListenerRef.current = null;
      }
      return;
    }

    const realtimePath = buildRealtimeChatPath();
    if (!realtimePath) return;

    setMessages([]);
    lastSentRealtimeKeysRef.current.clear();
    const chatRef = realtimeDbRef(realtimeDb, realtimePath);
    realtimeChatListenerRef.current = onChildAdded(chatRef, (snapshot) => {
      const msg = snapshot.val();
      const key = snapshot.key;
      if (!msg) return;

      if (key && lastSentRealtimeKeysRef.current.has(key)) {
        lastSentRealtimeKeysRef.current.delete(key);
        return;
      }
      if (msg.clientId && msg.clientId === realtimeClientIdRef.current) {
        return;
      }
      if (msg.sender === 'User' && !msg.clientId) {
        return;
      }

      setMessages((prev) => [
        ...prev,
        {
          sender: msg.sender || 'Unknown',
          text: msg.text || '',
          timestamp: msg.timestamp || Date.now(),
        },
      ]);
    });

    return () => {
      if (realtimeChatListenerRef.current) {
        realtimeChatListenerRef.current();
        realtimeChatListenerRef.current = null;
      }
    };
  }, [isRealtimeEnabled, chatPathKey, activeChatId, realtimeDb]);

  useEffect(() => {
    if (!isRealtimeEnabled) {
      if (realtimeUnsubscribeRef.current) {
        realtimeUnsubscribeRef.current();
        realtimeUnsubscribeRef.current = null;
      }
      return;
    }

    const { blocksCol } = resolveChatRefs();
    const q = query(blocksCol, orderBy('timestamp', 'desc'));
    realtimeUnsubscribeRef.current = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const data = change.doc.data() || {};
        const incomingId = data.id ?? change.doc.id;

        if (change.type === 'added') {
          setAnalysisBlocks((prevBlocks) => {
            const exists = prevBlocks.some(
              (blk) => String(blk.id) === String(incomingId)
            );
            if (exists) {
              return prevBlocks;
            }

            let normalizedBlock = normalizeAnalysisBlockDoc(data, incomingId);
            if (!hasBlockRank(normalizedBlock)) {
              normalizedBlock = {
                ...normalizedBlock,
                rank: allocateTopBlockRanks(1)[0],
              };
              setDoc(
                doc(blocksCol, String(incomingId)),
                { rank: normalizedBlock.rank },
                { merge: true }
              ).catch((error) => {
                console.error('Firestore rank migration error', error);
              });
            }
            registerBlockRanks([normalizedBlock]);

            return sortAnalysisBlocks([normalizedBlock, ...prevBlocks]);
          });
          return;
        }

        if (change.type === 'modified') {
          setAnalysisBlocks((prevBlocks) =>
            sortAnalysisBlocks(prevBlocks.map((blk) => {
              if (String(blk.id) !== String(incomingId)) {
                return blk;
              }

              return {
                ...blk,
                ...normalizeAnalysisBlockDoc(data, incomingId),
                runStatus: blk.runStatus ?? null,
                runStatusText: blk.runStatusText ?? '',
              };
            }))
          );
        }
      });
    });

    return () => {
      if (realtimeUnsubscribeRef.current) {
        realtimeUnsubscribeRef.current();
        realtimeUnsubscribeRef.current = null;
      }
    };
  }, [isRealtimeEnabled, chatId, chatPathKey]);

  return (
    <>
      <div 
        className="outer-container"
        style={{ 
          display: 'flex', 
          flexDirection: 'column',
          backgroundColor: '#1E1E1E',
          color: '#FFFFFF',
          padding: '16px',
          borderRadius: '8px',
        }}
      >
        {/* 1. Chat messages area */}
        <div
          className="chat-scroll"
          style={{
            height: '70vh',
            overflowY: 'scroll',
            padding: '10px',
            border: '0px solid #444',
            borderRadius: '8px',
            marginBottom: '10px',
            backgroundColor: '#1E1E1E',
          }}
        >
          {messages.map((message, index) => {
            const isUser = message.sender === 'User';
            return (
              <div
                key={index}
                style={{
                  margin: '5px 0',
                  textAlign: isUser ? 'right' : 'left',
                }}
              >
                <div
                  style={{
                    display: 'inline-block',
                    padding: '8px 12px',
                    borderRadius: '12px',
                    backgroundColor: isUser ? '#007BFF' : '#3A3A3A',
                    color: '#fff',
                    maxWidth: '70%',
                    wordWrap: 'break-word',
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                  }}
                >
                  <strong>{message.sender}:</strong>
                  <div>{message.text}</div>
                </div>
              </div>
            );
          })}

          {isThinking && (
            <div style={{ margin: '5px 0', textAlign: 'left', fontStyle: 'italic', color: '#aaa' }}>
              WUIGo-Bot: Thinking...
            </div>
          )}

        </div>

        {/* 2. Chat input area */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <TextareaAutosize
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type a message..."
              style={{
                flex: 1,
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #555',
                backgroundColor: '#2C2C2C',
                color: '#FFF',
                resize: 'none',
                fontFamily: 'inherit',
                lineHeight: '1.5',
              }}
              minRows={1}
            />
            <button
              onClick={handleSend}
              style={{
                height: '40px',
                padding: '0 16px',
                backgroundColor: '#007BFF',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Send
            </button>
          </div>
          <label className="analysis-code-version-toggle">
            <input
              type="checkbox"
              checked={useAnalysisCodeV2}
              onChange={(event) => setUseAnalysisCodeV2(event.target.checked)}
            />
            <span>Use V2</span>
          </label>
        </div>

        {/* 3. Analysis controls */}
        <div style={{ marginBottom: '20px', textAlign: 'left' }}>
          <div className="analysis-toolbar">
          <button
            onClick={() => setShowAnalysisDialog(true)}
            className="analysis-toolbar-button"
            title="Analysis script"
          >
            &#128221; Analysis script
          </button>

          <button
            onClick={() => setShowFilterPanel((prev) => !prev)}
            className={`analysis-toolbar-button ${showFilterPanel ? 'active' : ''}`}
            title="Filter analyses"
          >
            Filter
            {hasActiveFilter && <span className="analysis-filter-count">1</span>}
          </button>
          </div>

          {showFilterPanel && (
            <div className="analysis-filter-panel">
              <input
                type="text"
                className="analysis-filter-input"
                placeholder="Search question..."
                value={analysisFilterTerm}
                onChange={(e) => setAnalysisFilterTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    setShowFilterPanel(false);
                  }
                }}
                autoFocus
              />
              <div className="analysis-filter-actions">
                {analysisFilterTerm.length > 0 ? (
                  <button
                    type="button"
                    className="analysis-filter-clear"
                    onClick={handleClearFilter}
                  >
                    Clear
                  </button>
                ) : (
                  <button
                    type="button"
                    className="analysis-filter-clear"
                    onClick={handleCancelFilter}
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="button"
                  className="analysis-filter-done"
                  onClick={() => setShowFilterPanel(false)}
                >
                  Filter
                </button>
              </div>
            </div>
          )}

          {hasActiveFilter && (
            <>
              <div className="analysis-active-filters">
                <button
                  type="button"
                  className="analysis-filter-chip"
                  onClick={handleClearFilter}
                  title="Clear search filter"
                >
                  Search: "{analysisFilterTerm.trim()}"
                  <span className="analysis-filter-chip-close" aria-hidden="true">×</span>
                </button>
              </div>
              <div className="analysis-filter-summary">
                {filteredAnalysisBlocks.length} of {analysisBlocks.length} analyses
              </div>
            </>
          )}
        </div>

        {/* 4. Analysis blocks */}
        <div style={{ marginBottom: '20px' }}>
          {/* Only render the currently visible window of blocks to keep the DOM light. */}
          {filteredAnalysisBlocks.slice(0, visibleBlockCount).map((block) => {
            if (block.contentType === 'plan') {
              return (
                <PlanBlock
                  key={block.id}
                  block={block}
                  onToggleCollapse={handleToggleCollapse}
                  onDelete={handleDelete}
                />
              );
            }
            return (
                <AnalysisBlock 
                  key={block.id} 
                  block={block} 
                  allBlocks={analysisBlocks}
                onBlockClick={handleBlockClick} 
                onToggleCollapse={handleToggleCollapse}
                onRefresh={handleRefresh}
                onDelete={handleDelete} 
                onAddGraph={addGraphOnly}
                onRefreshMap={refreshMapOnly}
                onDeleteGraph={handleDeleteGraph}
                onRefreshCode={refreshCodeOnly}
                onFixCode={fixCodeOnly}
                onScaleOut={handleScaleOutClick}
                onMoveToPosition={moveAnalysisBlockToPosition}
                positionDisabled={hasActiveFilter}
              />
            );
          })}
          {hasActiveFilter && filteredAnalysisBlocks.length === 0 && (
            <div className="analysis-empty-state">
              No analyses match "{analysisFilterTerm.trim()}".
            </div>
          )}
          {/* Intersection observer watches this empty div; once visible we load more blocks. */}
          <div ref={sentinelRef} />
          {visibleBlockCount < filteredAnalysisBlocks.length && (
            <div style={{ textAlign: 'center', padding: '10px', color: '#aaa' }}>
              Loading more analyses…
            </div>
          )}
        </div>

        {latexContent && <LatexDisplay latexContent={latexContent} />}

        {analysisBlocks.length > 0 && (
          <button
            onClick={handleLatexConversion}
            style={{
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginBottom: '20px',
            }}
          >
            Convert to LaTeX
          </button>
        )}
      </div>

      {/* Modal Dialog for Analysis Script */}
      {showAnalysisDialog && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1E1E1E',
            padding: '20px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            color: '#fff'
          }}>
            <h3>Enter Analytical Instructions</h3>
            <TextareaAutosize
              value={analysisScriptText}
              onChange={(e) => setAnalysisScriptText(e.target.value)}
              placeholder="Type your analytical instructions here. Separate each block by a double new line."
              style={{
                width: '95%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #555',
                backgroundColor: '#2C2C2C',
                color: '#FFF',
                resize: 'vertical',
                fontFamily: 'inherit',
                lineHeight: '1.5',
              }}
              minRows={5}
            />
            <div style={{ marginTop: '10px', textAlign: 'right' }}>
              <button
                onClick={() => setShowAnalysisDialog(false)}
                style={{
                  marginRight: '10px',
                  padding: '6px 12px',
                  backgroundColor: '#777',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAnalysisScript}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#007BFF',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Dialog for Global Variables */}
      {showVariablesDialog && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#1E1E1E', padding: 20, borderRadius: 8,
            width: '90%', maxWidth: 500, color: '#fff'
          }}>
            <h3>Edit global variables</h3>

            <TextareaAutosize
              value={variablesText}
              onChange={e => setVariablesText(e.target.value)}
              placeholder="key1=value1\nkey2=value2 …"
              style={{
                width: '95%', padding: 8, borderRadius: 4,
                border: '1px solid #555', background: '#2C2C2C', color: '#FFF',
                resize: 'vertical', fontFamily: 'inherit', lineHeight: '1.5'
              }}
              minRows={6}
            />

            <div style={{ marginTop: 10, textAlign: 'right' }}>
              <button
                onClick={() => setShowVariablesDialog(false)}
                style={{
                  marginRight: 10, padding: '6px 12px',
                  background: '#777', color: '#fff',
                  border: 'none', borderRadius: 4, cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveGlobalVariables}
                style={{
                  padding: '6px 12px',
                  background: '#007BFF', color: '#fff',
                  border: 'none', borderRadius: 4, cursor: 'pointer'
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Dialog for Scale Out */}
      {showScaleDialog && scaleSourceBlock && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1E1E1E',
            padding: '20px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            color: '#fff'
          }}>
            <h3>Scale out analysis</h3>
            <p style={{ fontSize: '0.9rem', color: '#ccc' }}>
              Provide additional instructions for a new analysis that builds on the selected block.
            </p>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '10px',
                color: '#ddd',
                fontSize: '0.9rem',
              }}
            >
              <input
                type="checkbox"
                checked={scaleMode === 'single'}
                onChange={(e) => setScaleMode(e.target.checked ? 'single' : 'multi')}
              />
              Single-block scale out
              <span style={{ color: '#9ca3af' }}>
                ({scaleMode === 'single' ? 'Single-block' : 'Multi-block'})
              </span>
            </label>
            <TextareaAutosize
              value={scaleDialogText}
              onChange={(e) => setScaleDialogText(e.target.value)}
              placeholder="Describe how you'd like to scale out this analysis..."
              style={{
                width: '95%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #555',
                backgroundColor: '#2C2C2C',
                color: '#FFF',
                resize: 'vertical',
                fontFamily: 'inherit',
                lineHeight: '1.5',
              }}
              minRows={5}
            />
            <div style={{ marginTop: '10px', textAlign: 'right' }}>
              <button
                onClick={() => { setShowScaleDialog(false); setScaleSourceBlock(null); }}
                style={{
                  marginRight: '10px',
                  padding: '6px 12px',
                  backgroundColor: '#777',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveScaleOut}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#007BFF',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Run
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ChatComponent;
